import amplitude from "amplitude-js";
import { useEffect, useState } from "react";
import { amplitudeApiKey } from "src/config";

export const isTrackingDisabled = () => window.gloss?.disableTracking === true;

export type ConsentCategory = "analytics" | "marketing";

// True once the visitor allows the CookieHub category; never on credential
// routes. A withdrawal reloads the page, so it never goes back to false.
export const useConsented = (category: ConsentCategory) => {
  const [consented, setConsented] = useState(false);
  useEffect(
    () => window.gloss?.whenConsented?.(category, () => setConsented(true)),
    [category],
  );
  return consented;
};

let amplitudeStarted = false;
let amplitudeUserId: string | undefined;
// Events from before Amplitude starts (CookieHub initialises after the app
// mounts) wait here, in memory only, and go out if the visitor allows
// analytics during this page view.
// ponytail: capped at 50; anything later before consent is dropped.
const pendingEvents: [string, unknown][] = [];

export const isAmplitudeStarted = () => amplitudeStarted;

export const logAmplitudeEvent = (event: string, properties?: unknown) => {
  if (amplitudeStarted) {
    amplitude.getInstance().logEvent(event, properties);
  } else if (pendingEvents.length < 50) {
    pendingEvents.push([event, properties]);
  }
};

// Runs once the visitor allows analytics cookies (src/index.tsx).
export const startAmplitude = () => {
  // Ad click ids are marketing attribution, so they need marketing consent.
  const marketing = window.cookiehub?.hasConsented("marketing") === true;
  amplitude.getInstance().init(amplitudeApiKey, amplitudeUserId, {
    includeReferrer: true,
    includeUtm: true,
    includeGclid: marketing,
    includeFbclid: marketing,
  });
  amplitudeStarted = true;
  pendingEvents
    .splice(0)
    .forEach(([event, properties]) => logAmplitudeEvent(event, properties));
};

export const identifyAmplitudeUser = (userId: string) => {
  amplitudeUserId = userId;
  if (amplitudeStarted) {
    amplitude.getInstance().setUserId(userId);
  }
};
